import type { TemplateContext } from "@unbox/api";
import { Edit05 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { MessageComposer } from "@/components/message-composer/message-composer";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { resolveForPreview } from "@/utils/message-content";

type ModalCustomMessageExampleProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The policy's own copy for this step, unresolved. */
  message?: string;
  /** Copy the reviewer already wrote for this video, superseding `message`. */
  editedMessage?: string;
  previewContext?: TemplateContext;
  onViewPolicy?: () => void;
  /** Absent when the step can't be edited. `null` reverts to the policy's copy. */
  onSave?: (content: string | null) => void;
};

export const ModalCustomMessageExample = ({
  open,
  onOpenChange,
  message,
  editedMessage,
  previewContext,
  onViewPolicy,
  onSave,
}: ModalCustomMessageExampleProps) => {
  // Non-null while editing; holds the unsaved draft so closing the modal discards it.
  const [draft, setDraft] = useState<string | null>(null);

  const currentMessage = editedMessage ?? message;
  const isEdited = editedMessage !== undefined;

  useEffect(() => {
    if (!open) setDraft(null);
  }, [open]);

  const handleSave = () => {
    const trimmed = draft?.trim();
    if (!trimmed) return;
    // Typing the policy's copy back in is the same as never having edited it.
    onSave?.(trimmed === message?.trim() ? null : trimmed);
    setDraft(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Message that will be sent to creator</DialogTitle>
        </DialogHeader>

        {draft === null ? (
          <div className="min-w-0 bg-[#fffcf9] border border-border-primary rounded-md px-3.5 py-3 text-md text-primary leading-6 whitespace-pre-wrap break-words">
            {currentMessage && previewContext ? resolveForPreview(currentMessage, previewContext) : currentMessage}
          </div>
        ) : (
          <MessageComposer
            value={draft}
            onChange={setDraft}
            variableContext="safety"
            previewContext={previewContext}
            autoFocus
            aria-label="Message to send to the creator"
          />
        )}

        {draft === null ? (
          <DialogFooter className="items-center sm:justify-between">
            <p className="text-sm font-semibold text-secondary">
              {isEdited ? (
                <>
                  Edited for this video —{" "}
                  <button type="button" onClick={() => onSave?.(null)} className="underline decoration-dotted">
                    reset to the policy message
                  </button>
                </>
              ) : (
                <>
                  This message is defined by the{" "}
                  <button type="button" onClick={onViewPolicy} className="underline decoration-dotted">
                    safety policy
                  </button>
                </>
              )}
            </p>
            <div className="flex gap-1.5">
              <DialogClose asChild>
                <Button color="secondary">Close</Button>
              </DialogClose>
              {onSave && (
                <Button color="primary" iconLeading={Edit05} onClick={() => setDraft(currentMessage ?? "")}>
                  Edit message
                </Button>
              )}
            </div>
          </DialogFooter>
        ) : (
          <DialogFooter className="items-center sm:justify-between">
            <p className="text-sm text-tertiary">
              Applies to this video only. Variables are filled in when the message is sent.
            </p>
            <div className="flex gap-1.5">
              <Button color="secondary" onClick={() => setDraft(null)}>
                Discard
              </Button>
              <Button color="primary" onClick={handleSave} isDisabled={!draft.trim()}>
                Save changes
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
